import { useAppDispatch, useAppSelector } from '@/slices/hooks';
import {
  addNewMemberToOrganization,
  changeMemberRoleInOrganization,
  fetchCustomer,
  fetchMember,
  fetchOrganization,
  fetchOrganizationDetails,
  fetchPerson,
  removeMemberFromOrganization,
} from '@/slices/features/accountSlice';
import { MemberRole } from '@/actions/dtos/organisation';
import log from '@/utils/logger';

export function useAccount() {
  const dispatch = useAppDispatch();
  const { customer, person, organization, member, loading, error, organizationDetails } = useAppSelector(
    (state) => state.account,
  );
  const currentSubscription = customer?.currentSubscription;
  const currentTier = currentSubscription?.tier;
  const usage = currentSubscription?.usage;
  const limits = currentSubscription?.limits;

  const loadAllAccountData = async () => {
    await Promise.all([dispatch(fetchCustomer()), dispatch(fetchPerson()), dispatch(fetchMember())]);
    const fetchedOrganization = await dispatch(fetchOrganization());

    const organization = fetchedOrganization.payload;
    if (organization && typeof organization !== 'string' && organization.id) {
      await dispatch(fetchOrganizationDetails(organization.id));
    } else {
      log.error('Organization data was not fetched properly.');
    }
  };

  const refreshCustomer = () => dispatch(fetchCustomer());
  const refreshPerson = () => dispatch(fetchPerson());
  const refreshOrganization = () => dispatch(fetchOrganization());
  const refreshOrganizationDetails = () => dispatch(fetchOrganizationDetails(organization!.id));
  const refreshMember = () => dispatch(fetchMember());

  const addMember = async (email: string) => {
    if (organization) {
      await dispatch(addNewMemberToOrganization({ organizationId: organization.id, email }));
      refreshCustomer();
      refreshOrganization();
    }
  };

  const changeMemberRole = async (memberId: string, role: string, enabled: boolean) => {
    await dispatch(
      changeMemberRoleInOrganization({
        memberId,
        role,
        enabled,
        actionType: 'ROLE_CHANGE',
      }),
    );
    refreshOrganization();
  };

  const changeMemberStatus = async (memberId: string, role: string, enabled: boolean) => {
    await dispatch(
      changeMemberRoleInOrganization({
        memberId,
        role,
        enabled,
        actionType: 'STATUS_CHANGE',
      }),
    );
    refreshOrganization();
    refreshCustomer();
  };

  const deleteMember = async (memberId: string) => {
    dispatch(removeMemberFromOrganization({ memberId }));
    refreshCustomer();
    refreshOrganization();
  };

  const checkPermission = (roles: MemberRole[]) => {
    if (member) {
      const memberRole = member.role as unknown as keyof typeof MemberRole;
      return roles.includes(MemberRole[memberRole]);
    }
  };

  return {
    customer,
    person,
    organization,
    organizationDetails,
    member,
    currentTier,
    usage,
    limits,
    loading,
    error,
    checkPermission,
    loadAllAccountData,
    refreshCustomer,
    refreshPerson,
    refreshOrganization,
    refreshOrganizationDetails,
    refreshMember,
    addMember,
    changeMemberRole,
    changeMemberStatus,
    deleteMember,
  };
}
